import axios from 'axios';
import Config from '../_config/config';

// Type definitions
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  count?: number;
}

// Get auth token from localStorage
const getAuthToken = () => {
  const currentUser = localStorage.getItem('currentUser');
  if (currentUser && currentUser !== 'undefined') {
    try {
      const user = JSON.parse(currentUser);
      return user.token || '';
    } catch (error) {
      console.error('Failed to parse currentUser from localStorage:', error);
      return '';
    }
  }
  return '';
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: Config.nodeSiteUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to ensure proper type
apiClient.interceptors.response.use(
  (response) => {
    // Ensure response.data has the ApiResponse structure
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ==================== CENTRE MANAGEMENT ====================

export const getCentres = async (): Promise<ApiResponse> => {
  const response = await apiClient.get<ApiResponse>('v1/academic/centres');
  return response.data;
};

// ==================== CLASSROOM MANAGEMENT ====================

export const getClassrooms = async (params?: {
  centreId?: number;
  isActive?: number;
  minCapacity?: number;
  maxCapacity?: number;
  limit?: number;
  offset?: number;
}): Promise<ApiResponse> => {
  const response = await apiClient.get<ApiResponse>('v1/academic/classrooms', { params });
  return response.data;
};

export const createClassroom = async (data: {
  name: string;
  capacity: number;
  centreId: number;
  parentRoomId?: number | null;
  remarks?: string;
}): Promise<ApiResponse> => {
  const response = await apiClient.post<ApiResponse>('v1/academic/classrooms', data as any);
  return response.data;
};

export const updateClassroom = async (
  id: number,
  data: {
    name?: string;
    capacity?: number;
    parentRoomId?: number | null;
    remarks?: string;
  }
): Promise<ApiResponse> => {
  const response = await apiClient.put<ApiResponse>(`v1/academic/classrooms/${id}`, data as any);
  return response.data;
};

export const deleteClassroom = async (id: number): Promise<ApiResponse> => {
  const response = await apiClient.delete<ApiResponse>(`v1/academic/classrooms/${id}`);
  return response.data;
};

export const toggleClassroomStatus = async (id: number): Promise<ApiResponse> => {
  const response = await apiClient.post<ApiResponse>(`v1/academic/classrooms/${id}/toggle`);
  return response.data;
};

// ==================== SHIFT MANAGEMENT ====================

export const getShifts = async (params?: {
  limit?: number;
  offset?: number;
}): Promise<ApiResponse> => {
  const response = await apiClient.get<ApiResponse>('v1/academic/shifts', { params });
  return response.data;
};

export const createShift = async (data: {
  name: string;
  code?: string;
  startTime?: string;
  endTime?: string;
}): Promise<ApiResponse> => {
  const response = await apiClient.post<ApiResponse>('v1/academic/shifts', data as any);
  return response.data;
};

export const updateShift = async (
  id: number,
  data: {
    name?: string;
    code?: string;
    startTime?: string;
    endTime?: string;
  }
): Promise<ApiResponse> => {
  const response = await apiClient.put<ApiResponse>(`v1/academic/shifts/${id}`, data as any);
  return response.data;
};

export const deleteShift = async (id: number): Promise<ApiResponse> => {
  const response = await apiClient.delete<ApiResponse>(`v1/academic/shifts/${id}`);
  return response.data;
};

export const toggleShiftStatus = async (id: number): Promise<ApiResponse> => {
  const response = await apiClient.post<ApiResponse>(`v1/academic/shifts/${id}/toggle`);
  return response.data;
};

// ==================== CLASSROOM-SHIFT MAPPING ====================

export const getClassroomShiftMappings = async (params?: {
  classroomId?: number;
  shiftId?: number;
  isActive?: number;
  limit?: number;
  offset?: number;
}): Promise<ApiResponse> => {
  const response = await apiClient.get<ApiResponse>('v1/academic/classroom-shift-mappings', { params });
  return response.data;
};

export const createClassroomShiftMapping = async (data: {
  classroomId: number;
  shiftId: number;
  startDate: string;
  endDate?: string;
}): Promise<ApiResponse> => {
  const response = await apiClient.post<ApiResponse>('v1/academic/classroom-shift-mappings', data as any);
  return response.data;
};

export const updateClassroomShiftMapping = async (
  id: number,
  data: {
    startDate?: string;
    endDate?: string;
  }
): Promise<ApiResponse> => {
  const response = await apiClient.put<ApiResponse>(`v1/academic/classroom-shift-mappings/${id}`, data as any);
  return response.data;
};

export const deleteClassroomShiftMapping = async (id: number): Promise<ApiResponse> => {
  const response = await apiClient.delete<ApiResponse>(`v1/academic/classroom-shift-mappings/${id}`);
  return response.data;
};

export const getClassroomsByShift = async (shiftId: number): Promise<ApiResponse> => {
  const response = await apiClient.get<ApiResponse>(`v1/academic/shifts/${shiftId}/classrooms`);
  return response.data;
};

export const getShiftsByClassroom = async (classroomId: number): Promise<ApiResponse> => {
  const response = await apiClient.get<ApiResponse>(`v1/academic/classrooms/${classroomId}/shifts`);
  return response.data;
};

// ==================== SHIFT TIMESLOT MANAGEMENT ====================

export const getShiftTimeslots = async (shiftId: number): Promise<ApiResponse> => {
  const response = await apiClient.get<ApiResponse>(`v1/academic/shifts/${shiftId}/timeslots`);
  return response.data;
};

export const getAvailableTimeslots = async (shiftId: number, dayOfWeekId?: number): Promise<ApiResponse> => {
  const params = dayOfWeekId ? { dayOfWeekId } : {};
  const response = await apiClient.get<ApiResponse>(`v1/academic/shifts/${shiftId}/timeslots/available`, { params });
  return response.data;
};

export const addShiftTimeslots = async (
  shiftId: number,
  timeslotMappings: Array<{ timeSlotId: number; dayOfWeekId: number }>
): Promise<ApiResponse> => {
  const response = await apiClient.post<ApiResponse>(
    `v1/academic/shifts/${shiftId}/timeslots`,
    { timeslotMappings } as any
  );
  return response.data;
};

export const removeShiftTimeslots = async (shiftId: number, mappingIds: number[]): Promise<ApiResponse> => {
  const response = await apiClient.delete<ApiResponse>(
    `v1/academic/shifts/${shiftId}/timeslots`,
    { data: { mappingIds } } as any
  );
  return response.data;
};

// ==================== TIME SLOT MASTER ====================

export const getTimeSlots = async (params?: {
  limit?: number;
  offset?: number;
}): Promise<ApiResponse> => {
  const response = await apiClient.get<ApiResponse>('v1/academic/timeslots', { params });
  return response.data;
};

export const createTimeSlot = async (data: {
  name: string;
  startTime: string;
  endTime: string;
  attendanceFlag?: 0 | 1;
}): Promise<ApiResponse> => {
  const response = await apiClient.post<ApiResponse>('v1/academic/timeslots', data as any);
  return response.data;
};

export const updateTimeSlot = async (
  id: number,
  data: {
    name?: string;
    startTime?: string;
    endTime?: string;
    attendanceFlag?: 0 | 1;
  }
): Promise<ApiResponse> => {
  const response = await apiClient.put<ApiResponse>(`v1/academic/timeslots/${id}`, data as any);
  return response.data;
};

export const deleteTimeSlot = async (id: number): Promise<ApiResponse> => {
  const response = await apiClient.delete<ApiResponse>(`v1/academic/timeslots/${id}`);
  return response.data;
};

// ==================== CLASSROOM UTILIZATION REPORTS ====================

export const getUtilizationSummary = async (params: {
  startDate: string;
  endDate: string;
  centreId?: number;
  shiftId?: number;
}): Promise<ApiResponse> => {
  const response = await apiClient.get<ApiResponse>('v1/academic/classroom-utilization/summary', { params });
  return response.data;
};

export const getUtilizationByClassrooms = async (params: {
  startDate: string;
  endDate: string;
  centreId?: number;
  sortBy?: string;
  threshold?: number;
}): Promise<ApiResponse> => {
  const response = await apiClient.get<ApiResponse>('v1/academic/classroom-utilization/classrooms', { params });
  return response.data;
};

export const getUtilizationHeatmap = async (params: {
  startDate: string;
  endDate: string;
  centreId?: number;
}): Promise<ApiResponse> => {
  const response = await apiClient.get<ApiResponse>('v1/academic/classroom-utilization/heatmap', { params });
  return response.data;
};

export const getUtilizationRecommendations = async (params: {
  startDate: string;
  endDate: string;
  underutilizedThreshold?: number;
  overutilizedThreshold?: number;
}): Promise<ApiResponse> => {
  const response = await apiClient.get<ApiResponse>('v1/academic/classroom-utilization/recommendations', { params });
  return response.data;
};

const classroomShiftService = {
  // Centres
  getCentres,
  // Classrooms
  getClassrooms,
  createClassroom,
  updateClassroom,
  deleteClassroom,
  toggleClassroomStatus,
  // Shifts
  getShifts,
  createShift,
  updateShift,
  deleteShift,
  toggleShiftStatus,
  // Shift Timeslots
  getShiftTimeslots,
  getAvailableTimeslots,
  addShiftTimeslots,
  removeShiftTimeslots,
  // Time Slot Master
  getTimeSlots,
  createTimeSlot,
  updateTimeSlot,
  deleteTimeSlot,
  // Mappings
  getClassroomShiftMappings,
  createClassroomShiftMapping,
  updateClassroomShiftMapping,
  deleteClassroomShiftMapping,
  getClassroomsByShift,
  getShiftsByClassroom,
  // Utilization Reports
  getUtilizationSummary,
  getUtilizationByClassrooms,
  getUtilizationHeatmap,
  getUtilizationRecommendations,
};

export default classroomShiftService;
